import { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB } from '../../shared/styles';
import { sendOnboardingPushNotif } from '../../shared/dbService';
import { ONBOARDING_DAYS, getOnboardingDay } from './onboardingContent';

/**
 * OnboardingCoach
 * Renders once per day (days 1–7) after the user completes onboarding.
 * Shows a popup modal and injects a dashboard notification for the day.
 *
 * Props:
 *   lang         string  "nl" | "en"
 *   signedUpAt   string  "YYYY-MM-DD" stored in sl_signed_up
 *   onAddNotification  fn({type, text, time, read})
 *   onOpenLicense      fn() — opens the LicenseSheet for upgrade CTA
 *   onNav              fn(tabIndex)
 */
function OnboardingCoach({ lang, signedUpAt, onAddNotification, onOpenLicense, onNav }) {
  const nl = lang === "nl";
  const [visible, setVisible] = useState(false);
  const [dayContent, setDayContent] = useState(null);
  const addNotifRef = useRef(onAddNotification);
  useEffect(() => { addNotifRef.current = onAddNotification; });

  useEffect(() => {
    const dayIdx = getOnboardingDay(signedUpAt);
    if (dayIdx === null) return; // outside 7-day window

    const content = ONBOARDING_DAYS[dayIdx];
    if (!content) return;

    const dayNum = dayIdx + 1;
    const popupKey  = `sl_ob_popup_${dayNum}`;
    const notifKey  = `sl_ob_notif_${dayNum}`;

    // Inject notification + push once per day
    if (!localStorage.getItem(notifKey)) {
      const notifText = nl ? content.notifNL : content.notifEN;
      const notifTitle = nl ? `Sense Lifting — Dag ${dayNum}` : `Sense Lifting — Day ${dayNum}`;
      localStorage.setItem(notifKey, '1'); // set before calling to prevent double-fire on re-render
      addNotifRef.current({
        type: 'coach',
        text: notifText,
        time: 'now',
      });
      sendOnboardingPushNotif(notifTitle, notifText);
    }

    // Show popup once per day
    if (!localStorage.getItem(popupKey)) {
      setDayContent(content);
      setVisible(true);
    }
  }, [signedUpAt, nl]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => {
    if (!dayContent) return;
    localStorage.setItem(`sl_ob_popup_${dayContent.day}`, '1');
    setVisible(false);
  };

  const handleCta = () => {
    dismiss();
    if (dayContent?.ctaAction === 'upgrade') {
      onOpenLicense();
    } else if (dayContent?.ctaTab !== null && dayContent?.ctaTab !== undefined) {
      onNav(dayContent.ctaTab);
    }
  };

  if (!visible || !dayContent) return null;

  const acc = dayContent.accentColor;
  const isUpgrade = dayContent.isUpgradeDay;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 8000,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
      backdropFilter: 'blur(6px)',
    }}>
      <div style={{
        background: isUpgrade
          ? `linear-gradient(160deg,#0d1625 0%,#1a120a 100%)`
          : `linear-gradient(160deg,${_T.bg2} 0%,${_T.bg3} 100%)`,
        border: `1px solid ${acc}44`,
        borderRadius: 24,
        padding: '28px 22px 22px',
        maxWidth: 380,
        width: '100%',
        boxShadow: `0 0 60px ${acc}22, 0 24px 64px rgba(0,0,0,0.6)`,
        position: 'relative',
        overflow: 'hidden',
      }}>

        {/* Glow orb */}
        <div style={{
          position: 'absolute', top: -60, right: -60,
          width: 200, height: 200, borderRadius: '50%',
          background: `radial-gradient(circle,${acc}18,transparent 70%)`,
          pointerEvents: 'none',
        }}/>

        {/* Day badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: `${acc}18`, border: `1px solid ${acc}44`,
          borderRadius: 20, padding: '4px 12px',
          fontSize: 10, fontWeight: 700, letterSpacing: 2,
          color: acc, marginBottom: 14,
        }}>
          {isUpgrade ? '🚀' : `DAG ${dayContent.day}`}
          {isUpgrade && (nl ? 'UPGRADE' : 'UPGRADE')}
          {!isUpgrade && (nl ? ' VAN 7' : ' OF 7')}
        </div>

        {/* Icon + title */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
          <div style={{
            fontSize: 36, lineHeight: 1,
            filter: `drop-shadow(0 0 8px ${acc}88)`,
            flexShrink: 0,
          }}>
            {dayContent.icon}
          </div>
          <div style={{
            color: C.white, fontSize: 17, fontWeight: 700, lineHeight: 1.3,
          }}>
            {nl ? dayContent.titleNL : dayContent.titleEN}
          </div>
        </div>

        {/* Body */}
        <div style={{
          color: C.silver2, fontSize: 13, lineHeight: 1.75,
          marginBottom: 16,
          whiteSpace: 'pre-line',
        }}>
          {nl ? dayContent.bodyNL : dayContent.bodyEN}
        </div>

        {/* AHA moment card */}
        <div style={{
          background: `${acc}10`,
          border: `1px solid ${acc}30`,
          borderRadius: 12,
          padding: '10px 14px',
          marginBottom: 20,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: acc, letterSpacing: 1, marginBottom: 4 }}>
            {nl ? dayContent.ahaLabelNL : dayContent.ahaLabelEN}
          </div>
          <div style={{ fontSize: 12, color: C.silver2, lineHeight: 1.6 }}>
            {nl ? dayContent.ahaNL : dayContent.ahaEN}
          </div>
        </div>

        {/* Upgrade-day price highlight */}
        {isUpgrade && (
          <div style={{
            textAlign: 'center',
            marginBottom: 16,
          }}>
            <div style={{ color: acc, fontSize: 28, fontWeight: 800 }}>
              {nl ? '€7,99' : '€7.99'}
            </div>
            <div style={{ color: C.silver, fontSize: 11 }}>
              {nl ? 'per maand · opzegbaar wanneer je wil' : 'per month · cancel anytime'}
            </div>
          </div>
        )}

        {/* Primary CTA */}
        <button
          onClick={handleCta}
          style={{
            ...s_PB({ width: '100%', padding: '13px', fontSize: 14, borderRadius: 14 }),
            background: isUpgrade
              ? `linear-gradient(135deg,${acc},#E4C97A)`
              : undefined,
            color: isUpgrade ? '#000' : undefined,
            fontWeight: 700,
            marginBottom: isUpgrade ? 8 : 0,
          }}
        >
          {nl ? dayContent.ctaNL : dayContent.ctaEN}
        </button>

        {/* Secondary CTA for upgrade day */}
        {isUpgrade && (
          <button
            onClick={dismiss}
            style={{
              width: '100%', background: 'none', border: 'none',
              color: C.silver, fontSize: 12, cursor: 'pointer',
              padding: '8px', textDecoration: 'underline',
            }}
          >
            {nl ? dayContent.secondaryCtaNL : dayContent.secondaryCtaEN}
          </button>
        )}

        {/* Close button (non-upgrade days) */}
        {!isUpgrade && (
          <button
            onClick={dismiss}
            style={{
              position: 'absolute', top: 14, right: 14,
              background: 'none', border: 'none',
              color: C.silver, cursor: 'pointer', fontSize: 18,
              lineHeight: 1, padding: 4,
            }}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

export default OnboardingCoach;
